/**
 * AbonnementenPage — één overzicht van alle lopende service-abonnementen.
 *
 * Tot nu toe waren abonnementen alleen zichtbaar op de klantpagina
 * (AbonnementenSection), dus je moest wéten bij welke klant er een liep. Deze
 * pagina is het ontbrekende globale venster: wie, welk ritme, wanneer de
 * volgende cyclus valt en of de wissel gepland staat.
 *
 * Bewust READ-ONLY. Aanmaken, bewerken, pauzeren en opzeggen blijven op de
 * klantpagina — één schrijvende plek, geen tweede mutatiepad dat kan afwijken
 * (en de bewerk-dialoog heeft de order-context van die klant nodig).
 *
 * Data: GET /api/abonnementen zonder accountId geeft alles inclusief
 * `laatste_run`. Volume is klein (registry-tabel), dus alles in één keer
 * ophalen en client-side filteren.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { formatDate } from '../lib/format';

interface AbonnementTemplate {
  customer?: {
    firstName?: string;
    lastName?: string;
    company?: string | null;
    email?: string;
  } | null;
  address?: { postalCode?: string; city?: string } | null;
  kleden?: unknown[];
}

interface AbonnementRow {
  id: string;
  sf_account_id: string;
  naam: string;
  brand: string;
  interval_weeks: number;
  lead_days: number;
  next_run_at: string;
  eindigt_op: string | null;
  korting_percentage: number | string | null;
  bevestigingen_sturen: boolean;
  opmerkingen_planning: string | null;
  status: 'active' | 'paused' | 'cancelled';
  template: AbonnementTemplate | null;
  created_at?: string | null;
  created_by?: string | null;
  laatste_run: {
    period_key: string;
    status: string;
    sf_werk_order_name: string | null;
    error: string | null;
    fired_at: string;
    wissel_status: string | null;
    geplande_wissel_datum: string | null;
    is_extra: boolean | null;
  } | null;
}

type Filter = 'lopend' | 'active' | 'paused' | 'cancelled' | 'alles';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'lopend', label: 'Lopend' },
  { id: 'active', label: 'Actief' },
  { id: 'paused', label: 'Gepauzeerd' },
  { id: 'cancelled', label: 'Opgezegd' },
  { id: 'alles', label: 'Alles' },
];

/** Zelfde labels als AbonnementenSection — één semantiek in de hele app. */
const RUN_STATUS_LABELS: Record<string, string> = {
  created: 'order aangemaakt',
  'dry-run': 'dry-run',
  pending: 'bezig…',
  failed: 'mislukt',
};

const WISSEL_STATUS_LABELS: Record<string, string> = {
  'nog-niet': 'wissel nog niet gepland',
  'wacht-op-klaar': 'wacht op klaarmelding',
  'wacht-op-route': 'wacht op route',
  gepland: 'wissel gepland',
  handmatig: 'handmatig inplannen',
  'dry-run': 'wissel dry-run',
};

function wisselBadgeClass(status: string): string {
  switch (status) {
    case 'gepland':
      return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    case 'handmatig':
      return 'text-red-700 bg-red-50 border-red-200';
    case 'dry-run':
      return 'text-slate-600 bg-slate-50 border-slate-200';
    default:
      return 'text-amber-700 bg-amber-50 border-amber-200';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

/** Vandaag als YYYY-MM-DD in lokale tijd — next_run_at is een kale DATE. */
function vandaag(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const dag = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${dag}`;
}

/** YYYY-MM-DD → UTC-milliseconden; date-only, dus geen DST-drift. */
function dagAlsUtc(datum: string): number {
  const [y = 0, m = 1, d = 1] = datum.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Dagen tussen twee YYYY-MM-DD-strings (negatief = datum ligt in het verleden). */
function dagenTot(datum: string, vanaf: string): number {
  return Math.round((dagAlsUtc(datum) - dagAlsUtc(vanaf)) / 86_400_000);
}

function klantNaam(row: AbonnementRow): string {
  const c = row.template?.customer;
  const company = c?.company?.trim();
  if (company) return company;
  const naam = [c?.firstName, c?.lastName].filter(Boolean).join(' ').trim();
  return naam || 'Onbekende klant';
}

function frequentieLabel(weken: number): string {
  if (weken === 1) return 'Elke week';
  if (weken === 52) return 'Jaarlijks';
  if (weken === 26) return 'Halfjaarlijks';
  if (weken === 13) return 'Per kwartaal';
  if (weken === 4) return 'Elke 4 weken';
  return `Elke ${weken} weken`;
}

/** Hoeveel cycli dit abonnement per jaar oplevert — maakt ritmes vergelijkbaar. */
function perJaar(weken: number): string {
  const n = 52 / weken;
  return `${n % 1 === 0 ? n : n.toFixed(1).replace('.', ',')}× per jaar`;
}

function StatusPil({ status }: { status: AbonnementRow['status'] }) {
  const map: Record<AbonnementRow['status'], { label: string; cls: string }> = {
    active: { label: 'Actief', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
    paused: { label: 'Gepauzeerd', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
    cancelled: { label: 'Opgezegd', cls: 'text-slate-500 bg-slate-50 border-slate-200' },
  };
  const s = map[status];
  return (
    <span className={`text-[11px] font-medium border rounded px-1.5 py-0.5 whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Teller({ label, waarde, hint }: { label: string; waarde: string | number; hint?: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
      <div className="text-2xl font-semibold text-gray-900 tabular-nums">{waarde}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {hint && <div className="text-xs text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

export function AbonnementenPage() {
  const [rows, setRows] = useState<AbonnementRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('lopend');

  const load = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/abonnementen', { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Kon abonnementen niet laden');
      setRows((data.abonnementen as AbonnementRow[]) ?? []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Onverwachte fout');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const today = vandaag();

  const tellers = useMemo(() => {
    const actief = rows.filter((r) => r.status === 'active');
    const gepauzeerd = rows.filter((r) => r.status === 'paused');
    const opgezegd = rows.filter((r) => r.status === 'cancelled');
    // Een cyclus binnen 30 dagen is wat de planning raakt.
    const komende = actief.filter((r) => {
      const d = dagenTot(r.next_run_at, today);
      return d >= 0 && d <= 30;
    }).length;
    return { actief: actief.length, gepauzeerd: gepauzeerd.length, opgezegd: opgezegd.length, komende };
  }, [rows, today]);

  const zichtbaar = useMemo(() => {
    const gefilterd = rows.filter((r) => {
      if (filter === 'alles') return true;
      if (filter === 'lopend') return r.status !== 'cancelled';
      return r.status === filter;
    });
    // Eerstvolgende cyclus bovenaan; opgezegde onderaan (geen cyclus meer).
    return [...gefilterd].sort((a, b) => {
      if (a.status === 'cancelled' && b.status !== 'cancelled') return 1;
      if (b.status === 'cancelled' && a.status !== 'cancelled') return -1;
      return a.next_run_at.localeCompare(b.next_run_at);
    });
  }, [rows, filter]);

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Abonnementen</h1>
          <p className="text-gray-500 mt-1 max-w-3xl">
            Terugkerende diensten: elke cyclus maakt automatisch een order aan met verse prijzen, en de
            wissel-planner zet de ophaal- en terugbrengstop op dezelfde routedag. Aanmaken en wijzigen doe
            je op de klantpagina.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Teller label="Actief" waarde={tellers.actief} />
          <Teller label="Gepauzeerd" waarde={tellers.gepauzeerd} />
          <Teller label="Cyclus binnen 30 dagen" waarde={tellers.komende} />
          <Teller label="Opgezegd" waarde={tellers.opgezegd} />
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                filter === f.id
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start justify-between gap-4">
            <p className="text-red-800 text-sm">{error}</p>
            <button onClick={() => void load()} className="text-red-700 hover:text-red-900 text-sm font-medium">
              Opnieuw
            </button>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : zichtbaar.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-8 text-center">
            <p className="text-sm text-gray-500">
              {rows.length === 0
                ? 'Er lopen nog geen abonnementen. Maak er een aan vanaf een bestaande order op de klantpagina.'
                : 'Geen abonnementen in dit filter.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-2.5">Klant</th>
                    <th className="px-4 py-2.5">Abonnement</th>
                    <th className="px-4 py-2.5">Frequentie</th>
                    <th className="px-4 py-2.5">Volgende cyclus</th>
                    <th className="px-4 py-2.5">Laatste run</th>
                    <th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {zichtbaar.map((r) => {
                    const dagen = dagenTot(r.next_run_at, today);
                    const korting = r.korting_percentage == null ? null : Number(r.korting_percentage);
                    const stad = r.template?.address?.city?.trim();
                    const aantalKleden = Array.isArray(r.template?.kleden) ? r.template.kleden.length : 0;
                    const run = r.laatste_run;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50 align-top">
                        <td className="px-4 py-3">
                          <Link
                            to={`/klanten/${encodeURIComponent(r.sf_account_id)}?type=account`}
                            className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            {klantNaam(r)}
                          </Link>
                          {stad && <div className="text-xs text-gray-500 mt-0.5">{stad}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">{r.naam}</div>
                          <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-2">
                            <span>{r.brand}</span>
                            {aantalKleden > 0 && <span>{aantalKleden} {aantalKleden === 1 ? 'kleed' : 'kleden'}</span>}
                            {korting ? <span>{korting}% korting</span> : null}
                            {!r.bevestigingen_sturen && <span>geen klant-mails</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-gray-900">{frequentieLabel(r.interval_weeks)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{perJaar(r.interval_weeks)}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.status === 'cancelled' ? (
                            <span className="text-gray-400">—</span>
                          ) : (
                            <>
                              <div className="text-gray-900">{formatDate(r.next_run_at)}</div>
                              <div
                                className={`text-xs mt-0.5 ${dagen < 0 ? 'text-red-600 font-medium' : 'text-gray-500'}`}
                              >
                                {dagen < 0
                                  ? `${Math.abs(dagen)} dagen over tijd`
                                  : dagen === 0
                                    ? 'vandaag'
                                    : `over ${dagen} ${dagen === 1 ? 'dag' : 'dagen'}`}
                              </div>
                              {r.eindigt_op && (
                                <div className="text-xs text-gray-400 mt-0.5">t/m {formatDate(r.eindigt_op)}</div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {run ? (
                            <>
                              <div className="text-gray-700">
                                <span className={run.status === 'failed' ? 'text-red-600 font-medium' : ''}>
                                  {RUN_STATUS_LABELS[run.status] ?? run.status}
                                </span>
                                {run.sf_werk_order_name && (
                                  <span className="text-gray-500"> · {run.sf_werk_order_name}</span>
                                )}
                              </div>
                              {/* Wanneer de run draaide (fired_at) én voor welke cyclus
                                  (period_key). Alleen period_key tonen leest als een
                                  "laatste run" in de toekomst, en dus als een fout. */}
                              <div className="text-xs text-gray-500 mt-0.5">
                                {formatDate(run.fired_at)}
                                {run.period_key ? ` · cyclus ${formatDate(run.period_key)}` : ''}
                                {run.is_extra ? ' · extra wissel' : ''}
                              </div>
                              {run.wissel_status && run.wissel_status !== 'nog-niet' && (
                                <span
                                  className={`inline-block text-[11px] font-medium border rounded px-1.5 py-0.5 mt-1 ${wisselBadgeClass(run.wissel_status)}`}
                                >
                                  {WISSEL_STATUS_LABELS[run.wissel_status] ?? run.wissel_status}
                                  {run.geplande_wissel_datum &&
                                    (run.wissel_status === 'gepland' || run.wissel_status === 'dry-run') &&
                                    `: ${formatDate(run.geplande_wissel_datum)}`}
                                </span>
                              )}
                              {run.error && (
                                <div className="text-xs text-red-600 mt-1 max-w-xs truncate" title={run.error}>
                                  {run.error}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-400">nog geen run</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusPil status={r.status} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
